import React, { useState, useEffect } from 'react';
import {
  MessageSquare, Settings, Users, Folder, LogOut,
  BarChart2, Book, Tag, Bot, Clock, Send, UserSquare,
  MessageCircle, KeyRound, ChevronDown, Pencil, Check, X, Menu, Moon, Sun
} from 'lucide-react';
import api from '../services/api';
import ChangePasswordModal from './ChangePasswordModal';
import '../styles/Sidebar.css';

function getInitials(name = '') {
  return name.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?';
}

function Sidebar({ user, activeSection, onSelectSection, onLogout, whatsappStatus, onClose, className = '', unreadCounts = {} }) {
  const isAdmin = user.role === 'admin';
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [userDepts, setUserDepts] = useState([]);
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState(user.name || '');
  const [savingName, setSavingName] = useState(false);
  const [theme, setTheme] = useState(() =>
    (typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme')) === 'dark'
      ? 'dark' : 'light'
  );

  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    try {
      if (next === 'dark') {
        document.documentElement.setAttribute('data-theme', 'dark');
        localStorage.setItem('theme', 'dark');
      } else {
        document.documentElement.removeAttribute('data-theme');
        localStorage.setItem('theme', 'light');
      }
    } catch (e) {}
  };

  useEffect(() => {
    api.get('/users/profile')
      .then(r => setUserDepts(r.data.departments || []))
      .catch(() => {});
  }, []);

  const saveName = () => {
    if (!nameInput.trim() || nameInput.trim() === user.name) { setEditingName(false); return; }
    setSavingName(true);
    api.put('/users/profile/name', { name: nameInput.trim() })
      .then(r => {
        const stored = JSON.parse(localStorage.getItem('user') || '{}');
        stored.name = r.data.name;
        localStorage.setItem('user', JSON.stringify(stored));
        user.name = r.data.name;
        setEditingName(false);
      })
      .catch(() => {})
      .finally(() => setSavingName(false));
  };

  const waLabel = whatsappStatus === 'connected'
    ? 'WhatsApp conectado'
    : whatsappStatus === 'disconnected'
      ? 'WhatsApp desconectado'
      : 'WhatsApp — verificando...';

  const agentItems = [
    { key: 'conversations', label: 'Atendimentos',    icon: MessageSquare },
    { key: 'groups',        label: 'Grupos',          icon: Users },
    { key: 'contacts',      label: 'Contatos',        icon: UserSquare },
    { key: 'quick-replies', label: 'Respostas Rápidas', icon: Book },
    { key: 'scheduled',     label: 'Agendamentos',    icon: Send },
  ];

  const adminItems = [
    { key: 'overview',    label: 'Visão Geral',   icon: Users },
    { key: 'metrics',     label: 'Dashboard',     icon: BarChart2 },
    { key: 'tags',        label: 'Tags',          icon: Tag },
    { key: 'chatbot',     label: 'Chatbot & Menu', icon: Bot },
    { key: 'hours',       label: 'Horários',      icon: Clock },
    { key: 'whatsapp',    label: 'WhatsApp',      icon: Settings },
    { key: 'departments', label: 'Departamentos', icon: Folder },
    { key: 'users',       label: 'Usuários',      icon: Users },
  ];

  const renderItem = ({ key, label, icon: Icon }) => {
    const badge = unreadCounts[key] || 0;
    return (
      <button
        key={key}
        className={`nav-button ${activeSection === key ? 'active' : ''}`}
        onClick={() => onSelectSection(key)}
      >
        <Icon size={17} />
        <span>{label}</span>
        {badge > 0 && <span className="nav-badge">{badge > 99 ? '99+' : badge}</span>}
      </button>
    );
  };

  return (
    <aside className={`sidebar ${className}`}>
      {/* Brand */}
      <div className="sidebar-brand">
        <div className="sidebar-logo">
          <img src="/logo.png" alt="BDA Atendimentos" style={{width:'100%',height:'100%',objectFit:'contain',borderRadius:4}} onError={e=>{e.target.style.display='none'}} />
        </div>
        <div className="sidebar-brand-text">
          <h2>BDA&gt;&gt; Atendimentos</h2>
          <span>Central de Atendimento</span>
        </div>
        {onClose && (
          <button className="sidebar-close-btn" onClick={onClose} title="Fechar menu">
            <X size={18} />
          </button>
        )}
      </div>

      {/* Status do WhatsApp */}
      <div className="wa-status-bar">
        <span className={`wa-status-dot ${whatsappStatus || 'unknown'}`} />
        {waLabel}
      </div>

      {/* Usuário (clicável) */}
      <div className="sidebar-user" onClick={() => setShowUserMenu(v => !v)} title="Configurações do usuário">
        <div className="user-avatar">{getInitials(user.name)}</div>
        <div className="user-info">
          <p className="user-name">{user.name}</p>
          <span className={`user-role-badge ${isAdmin ? 'admin' : ''}`}>
            {isAdmin ? 'Administrador' : 'Agente'}
          </span>
        </div>
        <ChevronDown size={14} className={`user-chevron ${showUserMenu ? 'open' : ''}`} />
      </div>

      {/* Menu do usuário */}
      {showUserMenu && (
        <div className="user-menu-panel">
          {/* Nome editável */}
          <div className="user-menu-name-row">
            {editingName ? (
              <>
                <input
                  className="user-menu-name-input"
                  value={nameInput}
                  onChange={e => setNameInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') saveName(); if (e.key === 'Escape') setEditingName(false); }}
                  autoFocus
                  disabled={savingName}
                />
                <button className="user-menu-icon-btn" onClick={saveName} disabled={savingName} title="Salvar"><Check size={13} /></button>
                <button className="user-menu-icon-btn" onClick={() => setEditingName(false)} title="Cancelar"><X size={13} /></button>
              </>
            ) : (
              <>
                <span className="user-menu-display-name">{user.name}</span>
                <button className="user-menu-icon-btn" onClick={() => { setNameInput(user.name); setEditingName(true); }} title="Editar nome">
                  <Pencil size={13} />
                </button>
              </>
            )}
          </div>
          <div className="user-menu-depts">
            <span className="user-menu-depts-label">Meus departamentos</span>
            <div className="user-menu-depts-chips">
              {userDepts.length > 0
                ? userDepts.map(d => (
                    <span key={d.id} className="user-dept-chip">{d.name}</span>
                  ))
                : <span className="user-dept-none">Nenhum departamento</span>
              }
            </div>
          </div>
          <button
            className="user-menu-btn"
            onClick={() => { setShowUserMenu(false); setShowChangePassword(true); }}
          >
            <KeyRound size={13} />
            Trocar senha
          </button>
        </div>
      )}

      {/* Nav */}
      <nav className="sidebar-nav">
        <p className="nav-section-label">Geral</p>
        {agentItems.map(renderItem)}

        {isAdmin && (
          <>
            <div className="nav-divider" />
            <p className="nav-section-label">Administração</p>
            {adminItems.map(renderItem)}
          </>
        )}
      </nav>

      {/* Footer */}
      <div className="sidebar-footer">
        <button className="logout-button theme-toggle" onClick={toggleTheme} title={theme === 'dark' ? 'Modo claro' : 'Modo escuro'}>
          {theme === 'dark' ? <Sun size={16}/> : <Moon size={16}/>}
          <span>{theme === 'dark' ? 'Modo claro' : 'Modo escuro'}</span>
        </button>
        <button className="logout-button" onClick={onLogout}>
          <LogOut size={16} />
          <span>Sair da conta</span>
        </button>
      </div>

      {/* Modal de troca de senha */}
      {showChangePassword && (
        <ChangePasswordModal onClose={() => setShowChangePassword(false)} />
      )}
    </aside>
  );
}

export default Sidebar;
